import {
    ASSISTANT_INTENT,
    RECOMMENDATION_ACTION,
    RECOMMENDATION_MAX_AGE_MS,
    RECOMMENDATION_TIMING,
    RECOMMENDATION_TARGET,
} from 'src/contexts/assistant/constants';
import {
    TEMPLATE_CATEGORY_APPOINTMENT,
    TEMPLATE_CATEGORY_DOCUMENTATION,
    TEMPLATE_CATEGORY_FOLLOW_UP,
} from 'src/modules/conversations/constants/templates';
import {
    TRecommendation,
    TRecommendationOption,
} from 'src/modules/assistant/types/message';

const TIMING_BY_URGENCY: Readonly<Record<string, string>> = Object.freeze({
    today: RECOMMENDATION_TIMING.TODAY,
    soon: RECOMMENDATION_TIMING.NEXT_24_HOURS,
    none: RECOMMENDATION_TIMING.NO_RUSH,
});

export const getRecommendationTiming = (
    recommendation: Pick<TRecommendation, 'suggestedTiming' | 'urgency'>
): string =>
    recommendation.suggestedTiming ||
    TIMING_BY_URGENCY[recommendation.urgency] ||
    RECOMMENDATION_TIMING.NO_RUSH;

const TEMPLATE_CATEGORY_BY_ACTION: Readonly<Record<string, string>> =
    Object.freeze({
        [RECOMMENDATION_ACTION.CONFIRM_APPOINTMENT]:
            TEMPLATE_CATEGORY_APPOINTMENT,
        [RECOMMENDATION_ACTION.SCHEDULE_TEST_DRIVE]:
            TEMPLATE_CATEGORY_APPOINTMENT,
        [RECOMMENDATION_ACTION.SEND_QUOTE]: TEMPLATE_CATEGORY_DOCUMENTATION,
    });

export const getRecommendationTemplateCategory = (action: string): string =>
    TEMPLATE_CATEGORY_BY_ACTION[action] || TEMPLATE_CATEGORY_FOLLOW_UP;

export const getRecommendationTaskDraft = (
    recommendation: Pick<TRecommendation, 'title' | 'reason'>
): string =>
    [recommendation.title, recommendation.reason].filter(Boolean).join('. ');

const TEMPLATE_CAPABLE_ACTIONS = new Set<string>([
    RECOMMENDATION_ACTION.CONTACT,
    RECOMMENDATION_ACTION.FOLLOW_UP,
    RECOMMENDATION_ACTION.REACTIVATE,
    RECOMMENDATION_ACTION.SEND_QUOTE,
    RECOMMENDATION_ACTION.SEND_TEMPLATE,
]);

const getActionsByTarget = (
    action: string,
    executionTarget: string | undefined,
    hasConversation: boolean
): TRecommendationOption[] | null => {
    switch (executionTarget) {
        case RECOMMENDATION_TARGET.WHATSAPP_TEMPLATE:
            return [
                {
                    intent: ASSISTANT_INTENT.WHATSAPP,
                    label: 'Assistant.ActionWhatsApp',
                },
            ];
        case RECOMMENDATION_TARGET.CONVERSATION:
            return [
                ...(TEMPLATE_CAPABLE_ACTIONS.has(action)
                    ? [
                          {
                              intent: ASSISTANT_INTENT.WHATSAPP,
                              label: 'Assistant.ActionWhatsApp',
                          },
                      ]
                    : []),
                ...(hasConversation
                    ? [
                          {
                              intent: ASSISTANT_INTENT.CONVERSATION,
                              label: 'Assistant.ActionConversation',
                          },
                      ]
                    : []),
            ];
        case RECOMMENDATION_TARGET.APPOINTMENT:
            return [
                {
                    intent: ASSISTANT_INTENT.APPOINTMENT,
                    label: 'Assistant.ActionSchedule',
                },
            ];
        case RECOMMENDATION_TARGET.EXISTING_APPOINTMENT:
            return [
                {
                    intent: ASSISTANT_INTENT.VIEW_APPOINTMENT,
                    label: 'Assistant.ActionViewAppointment',
                },
            ];
        case RECOMMENDATION_TARGET.TASK:
            return [
                {
                    intent: ASSISTANT_INTENT.TASK,
                    label: 'Assistant.ActionFollowUpTask',
                },
            ];
        case RECOMMENDATION_TARGET.ASSIGNMENT:
            return [
                {
                    intent: ASSISTANT_INTENT.ASSIGN,
                    label: 'Assistant.ActionAssign',
                },
            ];
        case RECOMMENDATION_TARGET.NONE:
            return [];
        default:
            return null;
    }
};

export const getRecommendationActions = (
    action: string,
    hasConversation: boolean,
    executionTarget?: string
): TRecommendationOption[] => {
    if (action === RECOMMENDATION_ACTION.REACTIVATE)
        executionTarget = RECOMMENDATION_TARGET.WHATSAPP_TEMPLATE;
    const targetedActions = getActionsByTarget(
        action,
        executionTarget,
        hasConversation
    );
    if (targetedActions) return targetedActions;

    switch (action) {
        case RECOMMENDATION_ACTION.SCHEDULE_TEST_DRIVE:
            return [
                {
                    intent: ASSISTANT_INTENT.APPOINTMENT,
                    label: 'Assistant.ActionSchedule',
                },
            ];
        case RECOMMENDATION_ACTION.SEND_TEMPLATE:
            return [
                {
                    intent: ASSISTANT_INTENT.WHATSAPP,
                    label: 'Assistant.ActionWhatsApp',
                },
            ];
        case RECOMMENDATION_ACTION.ESCALATE:
            return [
                {
                    intent: ASSISTANT_INTENT.ASSIGN,
                    label: 'Assistant.ActionAssign',
                },
            ];
        case RECOMMENDATION_ACTION.CONFIRM_APPOINTMENT:
            return [
                {
                    intent: ASSISTANT_INTENT.VIEW_APPOINTMENT,
                    label: 'Assistant.ActionViewAppointment',
                },
                ...(hasConversation
                    ? [
                          {
                              intent: ASSISTANT_INTENT.CONVERSATION,
                              label: 'Assistant.ActionConversation',
                          },
                      ]
                    : []),
            ];
        case RECOMMENDATION_ACTION.CONTACT:
        case RECOMMENDATION_ACTION.FOLLOW_UP:
        case RECOMMENDATION_ACTION.REACTIVATE:
        case RECOMMENDATION_ACTION.SEND_QUOTE:
            return [
                {
                    intent: ASSISTANT_INTENT.WHATSAPP,
                    label: 'Assistant.ActionWhatsApp',
                },
                ...(hasConversation
                    ? [
                          {
                              intent: ASSISTANT_INTENT.CONVERSATION,
                              label: 'Assistant.ActionConversation',
                          },
                      ]
                    : []),
                {
                    intent: ASSISTANT_INTENT.TASK,
                    label: 'Assistant.ActionTask',
                },
            ];
        case RECOMMENDATION_ACTION.WAIT:
            return [
                {
                    intent: ASSISTANT_INTENT.TASK,
                    label: 'Assistant.ActionTask',
                },
            ];
        default:
            return [];
    }
};

export const isRecommendationStale = (
    recommendation: Pick<TRecommendation, 'generatedAt'>,
    leadUpdatedAt: string | Date | null | undefined,
    now: number = Date.now()
): boolean => {
    const generatedAt = new Date(recommendation.generatedAt).getTime();
    if (!Number.isFinite(generatedAt)) return false;
    if (now - generatedAt > RECOMMENDATION_MAX_AGE_MS) return true;
    const updatedAt = new Date(leadUpdatedAt).getTime();
    return Number.isFinite(updatedAt) && updatedAt > generatedAt;
};
